import { defineDocument } from './types.js'

/** Why a delivered quantity failed inspection (docs/24 §3.8). */
export const REJECTION_REASONS = [
  ['DAMAGED', 'Damaged in transit'],
  ['SPEC_MISMATCH', 'Not to specification'],
  ['WRONG_ITEM', 'Wrong item or make'],
  ['QUALITY_FAIL', 'Failed quality test'],
  ['NO_CERTIFICATE', 'No test certificate'],
  ['OTHER', 'Other'],
] as const satisfies ReadonlyArray<[string, string]>

/**
 * Added purely as metadata — no new service, controller, table or component.
 * This is Phase 1's exit criterion from docs/20: a second document type costs
 * one file. `allocatesFrom` is what puts its lines into the ledger against the
 * purchase order they receive.
 */
export const goodsReceipt = defineDocument({
  key: 'goods_receipt',
  group: 'Purchase and Expense',
  blurb: 'Receive materials from a supplier at the project site or warehouse against a purchase order.',
  route: 'grns',
  label: 'Receipt note (GRN)',
  labelPlural: 'Receipt notes (GRN)',
  numbering: 'GOODS_RECEIPT',
  numberPrefix: '{CO}/GRN#',
  workflow: 'GRN_2_STAGE',
  tabs: ['summary', 'related', 'form', 'drive'],

  createWizard: {
    title: 'Record a receipt note',
    subtitle: 'Choose the order the material arrived against. Quantities carry over — you only confirm what actually turned up.',
    fields: [
      { key: 'supplierId', label: 'Supplier', control: 'lookup', required: true, lookup: { entity: 'supplier' } },
      { key: 'purchaseOrderId', label: 'Purchase order', control: 'lookup', required: true, lookup: { entity: 'purchase_order' } },
      { key: 'warehouseId', label: 'Received at', control: 'lookup', required: true, lookup: { entity: 'warehouse' } },
    ],
  },

  form: {
    sections: [
      {
        key: 'general', n: '01', title: 'General information',
        fields: [
          { key: 'documentDate', label: 'Received on', control: 'date', required: true, width: 'half' },
          { key: 'supplierId', label: 'Supplier', control: 'lookup', required: true, width: 'half', lookup: { entity: 'supplier' }, lockAfterDraft: true },
          { key: 'purchaseOrderId', label: 'Against order', control: 'lookup', required: true, width: 'half', lookup: { entity: 'purchase_order' }, lockAfterDraft: true },
          { key: 'warehouseId', label: 'Received at', control: 'lookup', required: true, width: 'half', lookup: { entity: 'warehouse' } },
          // docs/24 §3.7 — the supplier's own advice of what was sent. Its
          // paperwork fills in below, and only what it sent is offered to pull.
          { key: 'supplierDispatchId', label: 'Against dispatch advice', control: 'lookup', width: 'half',
            lookup: { entity: 'supplier_dispatch' },
            hint: 'The supplier’s dispatch this delivery is. Its paperwork and quantities are carried over.' },
          { key: 'vehicleNo', label: 'Vehicle no', control: 'text', width: 'half' },
          { key: 'remarks', label: 'Remarks', control: 'textarea', width: 'full' },
        ],
      },
      /*
        docs/24 §3.8 — "material receipt at site, with the delivery challan,
        the purchase invoice and the test certificate". What came with the
        truck is recorded with the receipt, so a missing certificate is
        noticed at the gate rather than at billing.
      */
      {
        key: 'documents', n: '02', title: 'Documents received',
        fields: [
          { key: 'supplierDcNo', label: "Supplier's challan no", control: 'text', width: 'half' },
          { key: 'supplierInvoiceNo', label: "Supplier's invoice no", control: 'text', width: 'half' },
          { key: 'supplierInvoiceDate', label: "Supplier's invoice date", control: 'date', width: 'half' },
          { key: 'ewayBillNo', label: 'e-Way bill no', control: 'text', width: 'half' },
          { key: 'lrNo', label: 'LR / AWB no', control: 'text', width: 'half' },
          { key: 'testCertificateReceived', label: 'Test certificate / TDS received', control: 'checkbox', width: 'half',
            hint: 'The manufacturer’s test certificate or technical data sheet that has to come with the goods.' },
        ],
      },
      /*
        docs/24 §3.8 — "and quality inspection": who looked, when, and what
        they concluded. The per-line verdict is in the grid — delivered,
        rejected, why — and only what was accepted enters stock or counts
        against the order, so a rejected quantity stays open on the order
        for its replacement.
      */
      {
        key: 'inspection', n: '03', title: 'Quality inspection',
        fields: [
          { key: 'inspectedById', label: 'Inspected by', control: 'lookup', width: 'half', lookup: { entity: 'employee' } },
          { key: 'inspectedOn', label: 'Inspected on', control: 'date', width: 'half' },
          { key: 'qcStatus', label: 'Result', control: 'text', width: 'half',
            hint: 'Worked out from the lines when the receipt is saved.' },
          { key: 'inspectionRemarks', label: 'Inspection remarks', control: 'textarea', width: 'full' },
        ],
      },
    ],
  },

  lines: {
    title: 'Materials received', n: '04',
    columns: [
      { key: 'seq', label: '#', width: 46 },
      { key: 'itemId', label: 'Material', type: 'text' },
      { key: 'orderedQty', label: 'Ordered', type: 'qty', width: 104, align: 'right' },
      { key: 'openQty', label: 'Still open', type: 'qty', width: 108, align: 'right' },
      // What came off the truck, what failed inspection, and what was kept.
      { key: 'deliveredQty', label: 'Delivered', type: 'qty', width: 116, align: 'right', editable: true,
        hint: 'Everything that arrived, before inspection.' },
      { key: 'rejectedQty', label: 'Rejected', type: 'qty', width: 108, align: 'right', editable: true,
        hint: 'What failed inspection. It does not enter stock and stays open on the order.' },
      { key: 'rejectionReason', label: 'Why rejected', type: 'text', width: 180, editable: true,
        options: REJECTION_REASONS.map(([value, label]) => ({ value, label })) },
      { key: 'quantity', label: 'Accepted', type: 'qty', width: 116, align: 'right',
        formula: { subtract: ['deliveredQty', 'rejectedQty'], floor: '0' } },
      { key: 'unit', label: 'Unit', type: 'text', width: 86 },
      { key: 'binId', label: 'Bin', type: 'text', width: 150, editable: true, lookup: { entity: 'storage_bin' },
        hint: 'Where in the store it was put.' },
      // Carried down from the order, so the challan raised off this receipt
      // can split itself without anybody classifying the line again.
      { key: 'isBillable', label: 'Type', type: 'text', width: 124, editable: true,
        options: [{ value: 'YES', label: 'Billable' }, { value: 'NO', label: 'Non billable' }] },
      { key: 'rate', label: 'Rate', type: 'rate', width: 112, align: 'right' },
      { key: 'amount', label: 'Amount', type: 'money', width: 136, align: 'right' },
    ],
    sources: [{ key: 'order', label: 'Pull the open order lines', source: 'order' }],
  },

  columns: [
    { key: 'documentNo', label: 'Document no', type: 'docno', width: 152 },
    { key: 'documentDate', label: 'Received on', type: 'date', width: 138 },
    { key: 'supplierName', label: 'Supplier', type: 'text' },
    { key: 'projectName', label: 'Project', type: 'text' },
    { key: 'documentStatus', label: 'Status', type: 'status', width: 134 },
    { key: 'total', label: 'Value', type: 'money', width: 156, align: 'right' },
  ],

  filters: [
    { key: 'documentNo', label: 'Document no', control: 'text' },
    { key: 'documentDate', label: 'Received on', control: 'dateRange' },
    { key: 'documentStatus', label: 'Status', control: 'status' },
    { key: 'supplierId', label: 'Supplier', control: 'lookup', lookup: { entity: 'supplier' } },
  ],

  actions: ['save', 'submit', 'approve', 'reject', 'cancel', 'print'],
  rules: ['excess_receipt_quantity', 'brand_mismatch', 'rejection_without_reason', 'rejection_exceeds_delivery', 'inspection_missing'],
  allocatesFrom: ['purchase_order'],
  createsNext: [
    { docType: 'purchase_invoice', label: 'Purchase invoice', whenStatus: 'APPROVED' },
    // The receipt is where the billable/non-billable split first has both
    // kinds on one document, so it is where the two challans are raised from.
    // `splitBillable` on the challan then separates them (docs/video-notes/06 §2).
    { docType: 'delivery_challan', label: 'Delivery challan', whenStatus: 'APPROVED' },
    // docs/24 §3.8 — what failed inspection goes back, as a debit or for replacement.
    { docType: 'debit_note', label: 'Debit note', whenStatus: 'APPROVED' },
  ],
} as const)
